import {
  isRouteErrorResponse,
  Link,
  Links,
  Scripts,
  useRouteError,
} from "@remix-run/react";
import { lastEventId } from "@sentry/remix";
import { reportRouteError } from "~/lib/sentryReport";
import { AlertTriangle, Home } from "lucide-react";

/**
 * Shorten Sentry event IDs to something users can read/quote (still unique
 * enough to find in Sentry search).
 */
function formatReferenceId(eventId: string | undefined): string | null {
  if (!eventId) return null;
  return eventId.slice(0, 8);
}

function safeRouteErrorData(data: unknown): string {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (typeof data === "number" || typeof data === "boolean") {
    return String(data);
  }
  try {
    return JSON.stringify(data);
  } catch {
    return "";
  }
}

export function ErrorBoundary() {
  const error = useRouteError();
  reportRouteError(error);

  let title = "Something went wrong";
  let headline = "We hit a snag";
  let message =
    "Something unexpected happened. Please try again, or go back to the home page.";
  let status: number | null = null;
  /** Show Reference ID only for unexpected failures (5xx + thrown Errors), not for 4xx UX states. */
  let showReferenceId = false;

  if (isRouteErrorResponse(error)) {
    status = error.status;
    showReferenceId = error.status >= 500;
    const dataStr = safeRouteErrorData(error.data).trim();
    if (error.status === 404) {
      title = "Page not found";
      headline = "This page isn’t here";
      message =
        "The link may be broken or the page may have been removed.";
    } else if (error.status === 403) {
      title = "Access denied";
      headline = "You can’t open this";
      message =
        dataStr ||
        "You may need to sign in with a different account or return to a page you can access.";
    } else if (error.status === 401) {
      title = "Sign in required";
      headline = "Authentication needed";
      message =
        dataStr ||
        "Please sign in to continue, then try this page again.";
    } else {
      title = `Error ${error.status}`;
      headline = error.statusText || "Request error";
      message = dataStr || message;
    }
  } else if (error instanceof Error) {
    showReferenceId = true;
    message = import.meta.env.DEV
      ? error.message
      : "We’re sorry — please try again in a moment. If the problem continues, contact support.";
  }

  const referenceId = showReferenceId ? formatReferenceId(lastEventId()) : null;

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{title} · BulkBuyPlace</title>
        <Links />
      </head>
      <body className="min-h-screen bg-gradient-to-br from-emerald-50/80 via-white to-orange-50/60 text-slate-900 antialiased">
        <div className="flex min-h-screen flex-col">
          <header className="border-b border-slate-200/80 bg-white shadow-md">
            <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
              <Link
                to="/"
                aria-label="BulkBuyPlace home"
                className="relative z-20 flex h-12 min-w-0 max-w-full cursor-pointer items-center gap-2 rounded-md px-2 no-underline text-inherit hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring outline-offset-2"
              >
                <img
                  src="/images/cardboard_logo.svg"
                  className="h-10 w-10 shrink-0 select-none"
                  alt=""
                  draggable={false}
                />
                <span className="min-w-0 truncate font-bold text-lg tracking-wide select-none">
                  BulkBuyPlace
                </span>
              </Link>
              {status != null && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                  {status}
                </span>
              )}
            </div>
          </header>

          <main className="flex flex-1 flex-col items-center justify-center px-4 py-16">
            <div className="w-full max-w-md rounded-2xl border border-slate-200/90 bg-white/95 p-8 shadow-lg shadow-slate-200/50 backdrop-blur-sm">
              <div className="mb-6 flex justify-center">
                <AlertTriangle
                  className="size-12 text-red-600"
                  strokeWidth={2}
                  aria-hidden
                />
              </div>
              <h1 className="text-center text-2xl font-semibold tracking-tight text-slate-900">
                {headline}
              </h1>
              <p className="mt-3 text-center text-sm leading-relaxed text-slate-600">
                {message}
              </p>
              {import.meta.env.DEV &&
                error instanceof Error &&
                error.stack && (
                  <pre className="mt-6 max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-left text-xs text-slate-100">
                    {error.stack}
                  </pre>
                )}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Link
                  to="/"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-brand px-5 text-sm font-semibold text-white shadow transition hover:bg-brand/90"
                >
                  <Home className="size-4" aria-hidden />
                  Back to home
                </Link>
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="inline-flex h-11 items-center justify-center rounded-md border border-orange-200 bg-white px-5 text-sm font-medium text-orange-700 transition hover:bg-orange-50"
                >
                  Go back
                </button>
              </div>
              {referenceId && (
                <p className="mt-6 text-center text-xs text-slate-400">
                  Reference ID: <span className="font-mono">{referenceId}</span>
                </p>
              )}
            </div>
          </main>

          <footer className="border-t border-slate-200/80 bg-white/60 py-6 text-center text-xs text-slate-500">
            <Link to="/contact" className="text-brand underline-offset-2 hover:underline">
              Contact us
            </Link>
          </footer>
        </div>
        <Scripts />
      </body>
    </html>
  );
}
